import React from "react";
import { Gift } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

export const GIFT_THEMES = [
  { g: "from-[#0F766E] via-[#0D9488] to-[#134E4A]", accent: "#5EEAD4" },
  { g: "from-[#7C3AED] via-[#8B5CF6] to-[#4C1D95]", accent: "#C4B5FD" },
  { g: "from-[#B45309] via-[#D97706] to-[#7C2D12]", accent: "#FCD34D" },
  { g: "from-[#1D4ED8] via-[#2563EB] to-[#1E3A8A]", accent: "#93C5FD" },
  { g: "from-[#BE123C] via-[#E11D48] to-[#881337]", accent: "#FDA4AF" },
  { g: "from-[#166534] via-[#16A34A] to-[#14532D]", accent: "#86EFAC" },
];

// Occasion personalization themes (Round 115)
export const GIFT_OCCASIONS = {
  general: { label: "Just Because", emoji: "🎁", ribbon: "", decor: [] },
  birthday: { label: "Birthday", emoji: "🎂", ribbon: "HAPPY BIRTHDAY", decor: ["🎂", "🎈", "🎉", "🎊"],
              g: "from-[#DB2777] via-[#A21CAF] to-[#4A044E]", accent: "#F9A8D4" },
  graduation: { label: "Graduation", emoji: "🎓", ribbon: "CONGRATS GRADUATE", decor: ["🎓", "📜", "⭐", "🏅"],
                g: "from-[#1E40AF] via-[#312E81] to-[#111827]", accent: "#FCD34D" },
  congratulations: { label: "Congratulations", emoji: "🎉", ribbon: "CONGRATULATIONS", decor: ["🎉", "🏆", "🎊", "✨"],
                     g: "from-[#047857] via-[#065F46] to-[#022C22]", accent: "#FDE68A" },
};
const DECOR_POS = [
  { top: "8%", right: "26%", size: "16px", rot: "-14deg" },
  { bottom: "10%", right: "6%", size: "20px", rot: "12deg" },
  { top: "44%", left: "58%", size: "14px", rot: "8deg" },
  { bottom: "26%", left: "40%", size: "13px", rot: "-10deg" },
];

// Premium 3D digital gift card. `preview` renders a buyable package preview
// (no QR / serial / status — used on the shop grid).
export default function GiftCardVisual({ gift, compact = false, preview = false, offer = false }) {
  const t = GIFT_THEMES[(gift.design_idx || 0) % GIFT_THEMES.length];
  const occKey = gift.occasion && gift.occasion !== "general" ? gift.occasion : null;
  const occ = occKey ? GIFT_OCCASIONS[occKey] : null;
  const grad = occ?.g || t.g;
  const accent = occ?.accent || t.accent;
  const url = `${window.location.origin}/gift/${gift.token}`;
  if (offer) return (
    <div className="icaard-gift-offer-visual" data-testid={`gift-card-visual-${gift.id}`}>
      <div className={`icaard-gift-offer-art bg-gradient-to-br ${grad}`}>
        <span className="icaard-gift-offer-orb" aria-hidden="true" />
        <div className="icaard-gift-offer-top">
          <span className="icaard-gift-offer-logo"><img src="/icaard-logo-transparent.png" alt="iCaard" draggable={false}/></span>
          <span className="icaard-gift-offer-type">DIGITAL GIFT</span>
        </div>
        <div className="icaard-gift-offer-main">
          <div><span className="icaard-gift-offer-label">A thoughtful way to share</span><strong>{gift.package_name}</strong></div>
          <span className="icaard-gift-offer-icon"><Gift size={24}/></span>
        </div>
        <div className="icaard-gift-offer-bottom">
          <span>{(gift.components || []).map(c => c.type === "money" ? `GH₵${c.total}` : `${c.total} ${c.unit || c.label}`).join(" · ")}</span>
          <b>{fmtGiftAmount(gift.price)}</b>
        </div>
      </div>
    </div>
  );
  return (
    <div className="gift-3d" style={{ perspective: "1000px" }} data-testid={`gift-card-visual-${gift.id}`}>
      <div className={`relative rounded-3xl p-5 bg-gradient-to-br ${grad} overflow-hidden shadow-2xl transition-transform duration-300 hover:[transform:rotateX(4deg)_rotateY(-4deg)]`}
           style={{ boxShadow: "0 18px 40px -12px rgba(0,0,0,.6), inset 0 1px 0 rgba(255,255,255,.25)" }}>
        <div className="absolute -top-16 -right-16 w-52 h-52 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-20 -left-10 w-60 h-60 rounded-full bg-black/20 blur-2xl" />
        {occ && !compact && occ.decor.map((d, i) => (
          <span key={i} className="absolute select-none pointer-events-none opacity-30"
                style={{ ...DECOR_POS[i], fontSize: DECOR_POS[i].size, transform: `rotate(${DECOR_POS[i].rot})` }}>{d}</span>
        ))}
        <div className="relative flex items-start justify-between">
          <div>
            <div className="bg-white/90 rounded-lg px-1.5 py-1 inline-block">
              <img src="/icaard-logo-transparent.png" alt="iCaard" className="h-6 w-auto select-none" draggable={false} />
            </div>
            <div className="text-[8px] tracking-[0.4em] font-black text-white/70 mt-1">GIFT CARD</div>
            <div className="text-[7px] tracking-[0.1em] font-bold italic text-white/60 mt-0.5">"One Card. One System. Unlimited Access"</div>
          </div>
          <div className="bg-white rounded-xl p-1.5 shadow-lg">
            {preview ? (
              <div className="flex items-center justify-center" style={{ width: 46, height: 46 }}>
                <span className="text-3xl">🎁</span>
              </div>
            ) : (
              <QRCodeSVG value={url} size={compact ? 46 : 62} />
            )}
          </div>
        </div>
        {occ && (
          <div className="relative mt-2.5 -mx-5 py-1 text-center text-[9px] font-black tracking-[0.32em] text-white"
               style={{ background: "rgba(255,255,255,.14)", borderTop: "1px solid rgba(255,255,255,.18)", borderBottom: "1px solid rgba(255,255,255,.18)" }}
               data-testid={`gift-occasion-ribbon-${gift.id}`}>
            {occ.emoji} {occ.ribbon} {occ.emoji}
          </div>
        )}
        <div className="relative mt-3">
          <div className="font-display font-black text-white text-xl drop-shadow">{gift.package_name}</div>
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {(gift.components || []).map((c) => (
              <span key={c.key} className="text-[9px] font-black px-2 py-0.5 rounded-full bg-black/25 text-white border border-white/20">
                {c.type === "money" ? `GH₵${c.remaining.toFixed(0)}/${c.total.toFixed(0)}` : `${c.remaining}/${c.total} ${c.unit || c.label}`}
              </span>
            ))}
          </div>
        </div>
        {!compact && !preview && gift.wish && (
          <div className="relative mt-3 text-white">
            <div className="text-[11px] italic text-white/90">"{gift.wish}"</div>
          </div>
        )}
        {!preview && (
        <div className="relative flex items-center justify-between mt-3">
          <span className="text-[8px] font-mono text-white/60">{gift.serial || gift.token?.slice(0, 10)}</span>
          <span className="text-[8px] font-black px-2 py-0.5 rounded-full" style={{ background: "rgba(0,0,0,.3)", color: accent }}>
            {gift.status === "FULLY_USED" ? "EXPIRED" : (gift.status || "").replace(/_/g, " ")}
          </span>
        </div>
        )}
      </div>
    </div>
  );
}

function fmtGiftAmount(amount) {
  return `GH₵ ${Number(amount || 0).toLocaleString("en-GH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
